/**
 * GLAMRDiP viewport-aware anchor scroll.
 * Joel 2026-04-29.
 *
 * Opt-in via `data-scroll-center` on the target element. Click on any
 * `<a href="#id">` whose target carries the attribute is intercepted:
 *
 *   - Section FITS in viewport (height + 32px < viewport - header):
 *     scroll so the section is vertically CENTRED.
 *   - Section TALLER than viewport (always true on mobile for sections
 *     with images + body copy): scroll so the section's TOP sits below
 *     the sticky header (top-align with offset).
 *
 * Without the attribute, the link falls through to the browser's
 * default `:target { scroll-margin-top }` behaviour — same as before.
 */
(() => {
  'use strict';

  function getHeaderOffset() {
    const cs = getComputedStyle(document.documentElement);
    // Compose the offset from sticky-stack-h + promo-h since both can
    // pin elements above the scroll target.
    const stack  = cs.getPropertyValue('--gd-sticky-stack-h').trim() || '100px';
    const promo  = cs.getPropertyValue('--gd-promo-h').trim()       || '0px';
    // Quick parse — `calc()` values fall back to a sensible default.
    const parse = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 100;
    };
    // For calc() values we approximate by summing announcement-h + header-h.
    if (stack.startsWith('calc')) {
      const ah = parseFloat(cs.getPropertyValue('--gd-announcement-h')) || 50;
      const hh = parseFloat(cs.getPropertyValue('--gd-header-h')) || 64;
      return ah + hh + parse(promo);
    }
    return parse(stack) + parse(promo);
  }

  function scrollToTarget(target) {
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const targetTop    = rect.top + window.scrollY;
    const targetHeight = rect.height;
    const viewportH    = window.innerHeight;
    const headerOffset = getHeaderOffset();

    let finalScroll;
    if (targetHeight + 32 < viewportH - headerOffset) {
      // Section fits — vertically centre it.
      const targetCenter = targetTop + (targetHeight / 2);
      finalScroll = targetCenter - (viewportH / 2);
      // Floor: top of section never sits above the sticky header
      // (otherwise centring shoves the heading behind the chrome).
      const minScroll = targetTop - headerOffset - 16;
      if (finalScroll < minScroll) finalScroll = minScroll;
    } else {
      // Section taller than viewport — top-align below the sticky
      // chrome with a small breathing gap.
      finalScroll = targetTop - headerOffset - 16;
    }

    window.scrollTo({ top: Math.max(0, finalScroll), behavior: 'smooth' });
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#' || href.length <= 1) return;

    const id = href.slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    if (!target.hasAttribute('data-scroll-center')) return;  // opt-in

    e.preventDefault();
    scrollToTarget(target);
    if (typeof history.pushState === 'function') {
      history.pushState(null, '', href);
    } else {
      window.location.hash = href;
    }
  });
})();
